import React, { useState, useEffect } from 'react';
import {
  Button,
  Input,
  Radio,
  RadioGroup,
  Dialog,
  DialogSurface,
  DialogBody,
  DialogTitle,
  DialogContent,
  DialogActions,
  Card,
  CardPreview,
  Title3,
} from '@fluentui/react-components';
import { DismissRegular } from '@fluentui/react-icons';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import { Tendering, TenderingFeeMaxPenaltyFineType, TenderingFeePenaltyFineIntervalType } from '../../../models/tendering';
import { postExternal } from '../../../services/externalService';
import { convertToLocaleDateString } from '../../../utils/date';
import ErrorDialog from '../../dialogs/ErrorDialog';
import ContractFeeMeasureModal from '../dialogs/ContractFeeMeasureModal';
import ContractFeeMeasureList from './ContractFeeMeasureList';
import SuccessDialog from '../../dialogs/SuccessDialog';
import ConfirmationDialog from '../../dialogs/ConfirmationDialog';

interface TenderingAdminContractFeeProps {
  tendering: Tendering;
  onTenderingChange?: (tendering: Tendering) => void;
}

const TenderingAdminContractFee: React.FC<TenderingAdminContractFeeProps> = ({
  tendering,
  onTenderingChange,
}) => {
  const [contractFeeActive, setContractFeeActive] = useState(false);
  const [tenderingFeeStartDate, setTenderingFeeStartDate] = useState('');
  const [confirmActivationOpen, setConfirmActivationOpen] = useState(false);
  const [notifyTextDialogOpen, setNotifyTextDialogOpen] = useState(false);
  const [reminderTextDialogOpen, setReminderTextDialogOpen] = useState(false);
  const [billingTextDialogOpen, setBillingTextDialogOpen] = useState(false);
  const [measureModalOpen, setMeasureModalOpen] = useState(false);
  const [editMeasure, setEditMeasure] = useState<any>(null);
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [errorDialogOpen, setErrorDialogOpen] = useState(false);

  const periodOptions = [1, 2, 3, 4, 6, 12];

  const penaltyFineIntervalTypes = [
    'Enbart en gång',
    'Dag',
    'Vecka',
    'Månad',
  ];

  const maxPenaltyFineTypes = ['Fast', 'Andel av pris'];

  const reportTypes = ['Per kommun', 'Alla tillsammans'];

  useEffect(() => {
    setContractFeeActive(!!tendering.tenderingContractFee);
    if (tendering.tenderingContractFee && !tendering.tenderingContractFee.measures) {
      updateTendering({
        ...tendering,
        tenderingContractFee: {
          ...tendering.tenderingContractFee,
          measures: [],
        },
      });
    }
  }, []);

  const updateTendering = (updated: Tendering) => {
    if (onTenderingChange) {
      onTenderingChange(updated);
    }
  };

  const updateContractFee = (updates: any) => {
    const updated = {
      ...tendering,
      tenderingContractFee: {
        ...tendering.tenderingContractFee,
        ...updates,
      },
    };
    updateTendering(updated);
  };

  const activateContractFee = () => {
    const newContractFee = {
      feeStartDate: convertToLocaleDateString(tenderingFeeStartDate),
      reportPeriodInterval: 1,
      startDay: 15,
      nrOfDaysReportOpen: 30,
      hideOnOverdue: false,
      notifyText: '',
      notifyDay: -1,
      reminderText: '',
      reminderDay: 14,
      reminderInterval: 7,
      billingText: '',
      billingDay: 2,
      costCenter: '',
      account: '000-0000',
      object: '',
      deadline: 14,
      penaltyFineInterval: TenderingFeePenaltyFineIntervalType.Daily,
      penaltyFineAmount: 0,
      maxPenaltyFineType: TenderingFeeMaxPenaltyFineType.Fixed,
      maxPenaltyFineAmount: 0,
      measureReportType: reportTypes[0],
      measures: [],
      billingUrl: '',
    };

    updateTendering({
      ...tendering,
      tenderingContractFee: newContractFee,
    });
    setContractFeeActive(true);
    setConfirmActivationOpen(false);
  };

  const handleMeasureSave = (measure: any) => {
    if (!tendering.tenderingContractFee) return;
    const measures = tendering.tenderingContractFee.measures || [];
    
    if (editMeasure) {
      // Edit existing measure
      const updatedMeasures = measures.map((m: any) =>
        m.id === measure.id ? measure : m
      );
      updateContractFee({ measures: updatedMeasures });
    } else {
      // Add new measure
      updateContractFee({ measures: [...measures, measure] });
    }
    
    setMeasureModalOpen(false);
    setEditMeasure(null);
  };

  const handleMeasureEdit = (measure: any) => {
    setEditMeasure(measure);
    setMeasureModalOpen(true);
  };

  const handleMeasureDelete = (measure: any) => {
    if (!tendering.tenderingContractFee?.measures) return;
    const updatedMeasures = tendering.tenderingContractFee.measures.filter(
      (m: any) => m.id !== measure.id
    );
    updateContractFee({ measures: updatedMeasures });
  };

  const testBillingUrl = async () => {
    if (!tendering.tenderingContractFee?.billingUrl) return;
    
    const testObject = {
      tender: 'Test verksamhet',
      tendering: tendering.tenderingName,
    };

    try {
      await postExternal(
        tendering.tenderingContractFee.billingUrl,
        testObject
      );
      setSuccessDialogOpen(true);
    } catch (error) {
      console.error(error);
      setErrorDialogOpen(true);
    }
  };

  return (
    <Card>
      <CardPreview>
        <Title3>Avtalsadministrativ avgift</Title3>
      </CardPreview>
      <div className="overflow-y-auto space-y-4 w-full">
        {!contractFeeActive && (
          <div className="space-y-4">
          <p>
            Genom att aktivera avtalsadministrativ avgift kommer verksamheter att bli
            fakturerade utifrån inställningar som görs.
          </p>
          <p className="font-semibold">
            OBS denna handling går inte att ångra och måste vara förenlig med upphandlingen.
          </p>
          <div>
            <h5 className="text-sm font-semibold mb-2">
              Avtalsadministrativ startdatum: *
            </h5>
            <Input
              type="date"
              value={tenderingFeeStartDate}
              onChange={(e) => setTenderingFeeStartDate(e.target.value)}
              className="w-full max-w-xs"
            />
          </div>
          <Button
            appearance="primary"
            onClick={() => setConfirmActivationOpen(true)}
            disabled={!tenderingFeeStartDate}
          >
            Aktivera avtalsadministrativ avgift
          </Button>
        </div>
      )}

      {contractFeeActive && (
        <div className="space-y-6">
          {/* Periodicitet */}
          <div>
            <h5 className="text-sm font-semibold mb-2">Periodicitet (gånger år)</h5>
            <RadioGroup
              value={tendering.tenderingContractFee?.reportPeriodInterval?.toString()}
              onChange={(_, data) =>
                updateContractFee({ reportPeriodInterval: Number(data.value) })
              }
            >
              <div className="flex gap-4 flex-wrap">
                {periodOptions.map((p) => (
                  <Radio key={p} value={p.toString()} label={p.toString()} />
                ))}
              </div>
            </RadioGroup>
          </div>

          {/* Dag i månaden inrapportering börjar */}
          <div>
            <h5 className="text-sm font-semibold mb-2">
              Dag i månaden inrapportering börjar
            </h5>
            <Input
              type="number"
              value={tendering.tenderingContractFee?.startDay?.toString() || '15'}
              onChange={(e) => updateContractFee({ startDay: Number(e.target.value) })}
              min={1}
              max={31}
              className="w-32"
            />
          </div>

          {/* Dagar inrapportering är öppen */}
          <div>
            <h5 className="text-sm font-semibold mb-2">Dagar inrapportering är öppen</h5>
            <Input
              type="number"
              value={tendering.tenderingContractFee?.nrOfDaysReportOpen?.toString() || '30'}
              onChange={(e) =>
                updateContractFee({ nrOfDaysReportOpen: Number(e.target.value) })
              }
              min={1}
              max={365}
              className="w-32"
            />
          </div>

          {/* Vid försenad inrapportering dölj verksamhet */}
          <div>
            <h5 className="text-sm font-semibold mb-2">
              Vid försenad inrapportering dölj verksamhet
            </h5>
            <RadioGroup
              value={tendering.tenderingContractFee?.hideOnOverdue ? 'true' : 'false'}
              onChange={(_, data) =>
                updateContractFee({ hideOnOverdue: data.value === 'true' })
              }
            >
              <Radio value="false" label="Ej aktiverad" />
              <Radio value="true" label="Aktiv" />
            </RadioGroup>
          </div>

          {/* Notifieringstext */}
          <div>
            <Button appearance="secondary" onClick={() => setNotifyTextDialogOpen(true)}>
              Redigera notifieringstext
            </Button>
          </div>

          {/* Notifieringsdatum */}
          <div>
            <h5 className="text-sm font-semibold mb-2">Notifieringsdatum</h5>
            <Input
              type="number"
              value={tendering.tenderingContractFee?.notifyDay?.toString() || '-1'}
              onChange={(e) => updateContractFee({ notifyDay: Number(e.target.value) })}
              min={-100}
              max={100}
              className="w-32"
            />
          </div>

          {/* Påminnelsenotis meddelande */}
          <div>
            <Button
              appearance="secondary"
              onClick={() => setReminderTextDialogOpen(true)}
            >
              Redigera påminnelsenotis meddelande
            </Button>
          </div>

          {/* Påminnelsenotis start */}
          <div>
            <h5 className="text-sm font-semibold mb-2">
              Påminnelsenotis start (Antal dagar efter startdatum)
            </h5>
            <Input
              type="number"
              value={tendering.tenderingContractFee?.reminderDay?.toString() || '14'}
              onChange={(e) => updateContractFee({ reminderDay: Number(e.target.value) })}
              min={1}
              max={365}
              className="w-32"
            />
          </div>

          {/* Påminnelsenotis återkommande */}
          <div>
            <h5 className="text-sm font-semibold mb-2">
              Påminnelsenotis återkommande (Dagars mellanrum)
            </h5>
            <Input
              type="number"
              value={tendering.tenderingContractFee?.reminderInterval?.toString() || '7'}
              onChange={(e) =>
                updateContractFee({ reminderInterval: Number(e.target.value) })
              }
              min={1}
              max={365}
              className="w-32"
            />
          </div>

          {/* Faktureringstext */}
          <div>
            <Button appearance="secondary" onClick={() => setBillingTextDialogOpen(true)}>
              Redigera faktureringstext
            </Button>
          </div>

          {/* Faktureringsdag */}
          <div>
            <h5 className="text-sm font-semibold mb-2">
              Faktureringsdag (Antal dagar efter slutdatum)
            </h5>
            <Input
              type="number"
              value={tendering.tenderingContractFee?.billingDay?.toString() || '2'}
              onChange={(e) => updateContractFee({ billingDay: Number(e.target.value) })}
              min={1}
              max={365}
              className="w-32"
            />
          </div>

          {/* Kostnadsställe */}
          <div>
            <h5 className="text-sm font-semibold mb-2">Kostnadsställe</h5>
            <Input
              value={tendering.tenderingContractFee?.costCenter || ''}
              onChange={(e) => updateContractFee({ costCenter: e.target.value })}
              className="w-full"
            />
          </div>

          {/* Konto */}
          <div>
            <h5 className="text-sm font-semibold mb-2">Konto</h5>
            <Input
              value={tendering.tenderingContractFee?.account || '000-0000'}
              onChange={(e) => updateContractFee({ account: e.target.value })}
              className="w-full"
            />
          </div>

          {/* Objekt */}
          <div>
            <h5 className="text-sm font-semibold mb-2">Objekt</h5>
            <Input
              value={tendering.tenderingContractFee?.object || ''}
              onChange={(e) => updateContractFee({ object: e.target.value })}
              className="w-full"
            />
          </div>

          {/* Inställning för mätpunkter */}
          <div>
            <h5 className="text-sm font-semibold mb-2">Inställning för mätpunkter</h5>
            <ContractFeeMeasureList
              tendering={tendering}
              measures={tendering.tenderingContractFee?.measures || []}
              onEdit={handleMeasureEdit}
              onDelete={handleMeasureDelete}
            />
          </div>

          {/* Inrapportering per beställare */}
          <div>
            <h5 className="text-sm font-semibold mb-2">Inrapportering per beställare</h5>
            <RadioGroup
              value={tendering.tenderingContractFee?.measureReportType || reportTypes[0]}
              onChange={(_, data) => updateContractFee({ measureReportType: data.value })}
            >
              {reportTypes.map((type) => (
                <Radio key={type} value={type} label={type} />
              ))}
            </RadioGroup>
          </div>

          {/* Lägg till mätpunkt */}
          <div>
            <Button
              appearance="secondary"
              onClick={() => {
                setEditMeasure(null);
                setMeasureModalOpen(true);
              }}
            >
              Lägg till mätpunkt
            </Button>
          </div>

          {/* Fakturering URL */}
          <div>
            <h5 className="text-sm font-semibold mb-2">Fakturering URL</h5>
            <Input
              value={tendering.tenderingContractFee?.billingUrl || ''}
              onChange={(e) => updateContractFee({ billingUrl: e.target.value })}
              className="w-full mb-2"
            />
            <Button appearance="secondary" onClick={testBillingUrl}>
              Testa URL
            </Button>
          </div>
        </div>
      )}

      {/* Confirmation Dialog for Activation */}
      <ConfirmationDialog
        isOpen={confirmActivationOpen}
        onClose={() => setConfirmActivationOpen(false)}
        onConfirm={activateContractFee}
        title="Aktivera avtalsadministrativ avgift"
        text={`Säker på att du vill aktivera leverantörsbetalningar fr.o.m ${convertToLocaleDateString(
          tenderingFeeStartDate
        )}? Denna handling går inte att ångra.`}
      />

      {/* Notify Text Dialog */}
      <Dialog
        open={notifyTextDialogOpen}
        onOpenChange={(_, data) => !data.open && setNotifyTextDialogOpen(false)}
      >
        <DialogSurface className="max-w-[600px]">
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="close"
                  icon={<DismissRegular />}
                  onClick={() => setNotifyTextDialogOpen(false)}
                />
              }
            >
              Redigera notifieringstext
            </DialogTitle>
            <DialogContent className="min-h-[300px]">
              <ReactQuill
                theme="snow"
                value={tendering.tenderingContractFee?.notifyText || ''}
                onChange={(value) => updateContractFee({ notifyText: value })}
                style={{ height: '180px', marginBottom: '50px' }}
              />
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                onClick={() => setNotifyTextDialogOpen(false)}
              >
                OK
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Reminder Text Dialog */}
      <Dialog
        open={reminderTextDialogOpen}
        onOpenChange={(_, data) => !data.open && setReminderTextDialogOpen(false)}
      >
        <DialogSurface className="max-w-[600px]">
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="close"
                  icon={<DismissRegular />}
                  onClick={() => setReminderTextDialogOpen(false)}
                />
              }
            >
              Redigera påminnelsenotis
            </DialogTitle>
            <DialogContent className="min-h-[300px]">
              <ReactQuill
                theme="snow"
                value={tendering.tenderingContractFee?.reminderText || ''}
                onChange={(value) => updateContractFee({ reminderText: value })}
                style={{ height: '180px', marginBottom: '50px' }}
              />
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                onClick={() => setReminderTextDialogOpen(false)}
              >
                OK
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Billing Text Dialog */}
      <Dialog
        open={billingTextDialogOpen}
        onOpenChange={(_, data) => !data.open && setBillingTextDialogOpen(false)}
      >
        <DialogSurface className="max-w-[600px]">
          <DialogBody>
            <DialogTitle
              action={
                <Button
                  appearance="subtle"
                  aria-label="close"
                  icon={<DismissRegular />}
                  onClick={() => setBillingTextDialogOpen(false)}
                />
              }
            >
              Redigera faktureringstext
            </DialogTitle>
            <DialogContent className="min-h-[300px]">
              <ReactQuill
                theme="snow"
                value={tendering.tenderingContractFee?.billingText || ''}
                onChange={(value) => updateContractFee({ billingText: value })}
                style={{ height: '180px', marginBottom: '50px' }}
              />
            </DialogContent>
            <DialogActions>
              <Button
                appearance="primary"
                onClick={() => setBillingTextDialogOpen(false)}
              >
                OK
              </Button>
            </DialogActions>
          </DialogBody>
        </DialogSurface>
      </Dialog>

      {/* Measure Modal */}
      {measureModalOpen && (
        <ContractFeeMeasureModal
          isOpen={measureModalOpen}
          onClose={() => {
            setMeasureModalOpen(false);
            setEditMeasure(null);
          }}
          onSave={handleMeasureSave}
          tenderingParts={tendering.tenderingParts}
          measure={editMeasure}
        />
      )}

      {/* Success/Error Dialogs */}
      <SuccessDialog
        isOpen={successDialogOpen}
        onClose={() => setSuccessDialogOpen(false)}
      />
      <ErrorDialog isOpen={errorDialogOpen} onClose={() => setErrorDialogOpen(false)} />
      </div>
    </Card>
  );
};

export default TenderingAdminContractFee;
